import { Droplets, Pencil, Plus, TriangleAlert } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { EmptyState } from "../components/EmptyState";
import { PoolReadingForm, leggiMisura } from "../components/PoolReadingForm";
import { PrintButton, PrintSheetHeading } from "../components/PrintSheet";
import { formatLiters, POOL_ACTIONS, poolActionShortLabels, totalRefillLiters, type PoolActionKey } from "../../shared/pool-actions";
import { isOutOfRange, type ReadingVerdict } from "../../shared/pool-thresholds";
import { normalizeTimeZone, zonedWallClockToUtc } from "../../shared/time-zone";
import { addDays } from "../../shared/week";
import type { RouterOutputs, UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";

type Pool = RouterOutputs["pools"]["list"][number];

function valore(numero: number | null, verdetto: ReadingVerdict) {
  if (numero == null) return <span className="pool-value pool-value--vuoto">non misurato</span>;
  const fuori = isOutOfRange(verdetto);
  return (
    <span className={`pool-value${fuori ? " pool-value--fuori" : ""}`}>
      {numero}
      {fuori && <TriangleAlert size={14} aria-label={`fuori soglia, ${verdetto} l'intervallo`} />}
    </span>
  );
}

function quando(data: Date | string) {
  return new Date(data).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Sul foglio del cliente giorno e ora sono due colonne, non una scritta sola. */
function giorno(data: Date | string) {
  return new Date(data).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function ora(data: Date | string) {
  return new Date(data).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

/** Il periodo scritto come lo legge chi riceve il foglio. */
function periodoLeggibile(dal: string, al: string) {
  if (!dal && !al) return "Tutto lo storico";
  if (dal && al) return `Dal ${giorno(`${dal}T12:00:00`)} al ${giorno(`${al}T12:00:00`)}`;
  return dal ? `Dal ${giorno(`${dal}T12:00:00`)}` : `Fino al ${giorno(`${al}T12:00:00`)}`;
}

export function PoolsPage({ session }: { session: UserSession }) {
  const canManage = session.membership.role !== "collaborator";
  const utils = trpc.useUtils();
  const pools = trpc.pools.list.useQuery();
  const timeZone = normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [siteId, setSiteId] = useState<string>("");
  const [poolId, setPoolId] = useState<string>("");
  const [dal, setDal] = useState<string>("");
  const [al, setAl] = useState<string>("");
  const [azione, setAzione] = useState<PoolActionKey | "">("");
  // Il giorno scelto diventa un istante nel fuso di chi guarda, come nel registro ore: il
  // primo istante del giorno iniziale e l'ultimo di quello finale, estremi compresi.
  const readings = trpc.pools.readings.useQuery({
    siteId: siteId || null,
    poolId: poolId || null,
    from: dal ? zonedWallClockToUtc(dal, 0, timeZone) : null,
    to: al ? new Date(zonedWallClockToUtc(addDays(al, 1), 0, timeZone).getTime() - 1) : null,
    action: azione || null,
    limit: 500,
  });
  const sites = trpc.sites.list.useQuery(undefined, { enabled: canManage });
  const create = trpc.pools.create.useMutation({ onSuccess: () => void utils.pools.invalidate() });
  const update = trpc.pools.update.useMutation({ onSuccess: () => void utils.pools.invalidate() });
  const [editing, setEditing] = useState<Pool | null>(null);
  const [openForm, setOpenForm] = useState(false);
  const salvataggio = editing ? update : create;

  const perStruttura = useMemo(() => {
    const gruppi = new Map<string, { siteId: string; siteName: string; pools: Pool[] }>();
    for (const piscina of pools.data ?? []) {
      const gruppo = gruppi.get(piscina.siteId) ?? { siteId: piscina.siteId, siteName: piscina.siteName, pools: [] };
      gruppo.pools.push(piscina);
      gruppi.set(piscina.siteId, gruppo);
    }
    return [...gruppi.values()];
  }, [pools.data]);
  const attive = useMemo(() => (pools.data ?? []).filter((piscina) => piscina.active), [pools.data]);
  const vascheFiltrabili = useMemo(
    () => (pools.data ?? []).filter((piscina) => !siteId || piscina.siteId === siteId),
    [pools.data, siteId],
  );
  const strutturaScelta = perStruttura.find((gruppo) => gruppo.siteId === siteId);
  const vascaScelta = (pools.data ?? []).find((piscina) => piscina.id === poolId);
  const azioneScelta = POOL_ACTIONS.find((voce) => voce.key === azione);
  const letture = readings.data ?? [];
  const litriDelPeriodo = totalRefillLiters(letture);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const input = {
      siteId: String(data.get("siteId")),
      name: String(data.get("name")),
      chlorineMin: leggiMisura(String(data.get("chlorineMin") ?? "")) ?? 0,
      chlorineMax: leggiMisura(String(data.get("chlorineMax") ?? "")) ?? 0,
      phMin: leggiMisura(String(data.get("phMin") ?? "")) ?? 0,
      phMax: leggiMisura(String(data.get("phMax") ?? "")) ?? 0,
    };
    try {
      if (editing) await update.mutateAsync({ ...input, id: editing.id, active: data.get("active") === "on" });
      else await create.mutateAsync(input);
      setOpenForm(false);
      setEditing(null);
    } catch {
      // L'errore resta nella mutazione e la scheda lo mostra sotto al modulo.
    }
  }

  return (
    <main className="page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Registro piscine</p>
          <h1>Piscine</h1>
          <p>Cloro e pH struttura per struttura, con lo storico di quello che e stato misurato.</p>
        </div>
        {canManage && (
          <div className="page-heading__actions">
            <button className="button button--primary" type="button" onClick={() => { setEditing(null); setOpenForm(true); }}>
              <Plus size={19} /> Nuova piscina
            </button>
          </div>
        )}
      </header>

      {openForm && canManage && (
        <form className="editor-card" onSubmit={submit}>
          <div className="editor-card__heading">
            <h2>{editing ? "Modifica la piscina" : "Aggiungi una piscina"}</h2>
            <p>Gli intervalli sono quelli che l'azienda considera normali per quella vasca.</p>
          </div>
          <div className="form-grid">
            <label><span>Nome</span><input name="name" required minLength={1} defaultValue={editing?.name ?? ""} placeholder="Piscina grande" /></label>
            <label>
              <span>Struttura</span>
              <select name="siteId" required defaultValue={editing?.siteId ?? ""}>
                {sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
              </select>
            </label>
            <label><span>Cloro minimo in mg/l</span><input name="chlorineMin" inputMode="decimal" defaultValue={editing?.chlorine.min ?? "0.5"} /></label>
            <label><span>Cloro massimo in mg/l</span><input name="chlorineMax" inputMode="decimal" defaultValue={editing?.chlorine.max ?? "1.5"} /></label>
            <label><span>pH minimo</span><input name="phMin" inputMode="decimal" defaultValue={editing?.ph.min ?? "7.2"} /></label>
            <label><span>pH massimo</span><input name="phMax" inputMode="decimal" defaultValue={editing?.ph.max ?? "7.6"} /></label>
            {editing && (
              <label className="check-control check-control--inline">
                <input type="checkbox" name="active" defaultChecked={editing.active} /> In servizio
              </label>
            )}
          </div>
          {salvataggio.error && <p className="form-error">{salvataggio.error.message}</p>}
          <div className="form-actions">
            <button className="button button--ghost" type="button" onClick={() => { setOpenForm(false); setEditing(null); }}>Annulla</button>
            <button className={`button button--primary${salvataggio.isPending ? " button--saving" : ""}`}>
              {salvataggio.isPending ? "Salvataggio..." : editing ? "Salva la piscina" : "Aggiungi la piscina"}
            </button>
          </div>
        </form>
      )}

      {attive.length > 0 && (
        <section className="detail-card">
          <PoolReadingForm pools={attive} onSaved={() => void utils.pools.invalidate()} />
        </section>
      )}

      {/* La configurazione delle vasche resta a schermo e sparisce dal foglio: al condominio
          si consegna il registro delle letture, non le soglie che l'azienda si e data. */}
      <section className="section-block pools-section">
        <div className="section-heading"><div><p className="eyebrow">Vasche</p><h2>Le piscine seguite</h2></div></div>
        {pools.isLoading && <p className="loading-copy">Caricamento piscine...</p>}
        {!pools.isLoading && perStruttura.length === 0 && (
          <EmptyState title="Nessuna piscina">
            {canManage ? "Aggiungi la prima vasca e i suoi intervalli di cloro e pH." : "Nessuna piscina sulle strutture che segui."}
          </EmptyState>
        )}
        {perStruttura.map((gruppo) => (
          <section className="pool-group" key={gruppo.siteId}>
            <h3><Droplets size={18} aria-hidden="true" /> {gruppo.siteName}</h3>
            <ul className="pool-list">
              {gruppo.pools.map((piscina) => (
                <li className={`pool-card${piscina.active ? "" : " pool-card--ferma"}`} key={piscina.id}>
                  <div>
                    <strong>{piscina.name}{piscina.active ? "" : " (fuori servizio)"}</strong>
                    <small>Cloro da {piscina.chlorine.min} a {piscina.chlorine.max} mg/l · pH da {piscina.ph.min} a {piscina.ph.max}</small>
                  </div>
                  <div className="pool-card__last">
                    {piscina.lastReadingAt
                      ? <>
                        <span>Cloro {valore(piscina.lastChlorine, piscina.lastChlorineVerdict)} · pH {valore(piscina.lastPh, piscina.lastPhVerdict)}</span>
                        <small>Ultima lettura {quando(piscina.lastReadingAt)}</small>
                      </>
                      : <small>Mai misurata</small>}
                  </div>
                  {canManage && (
                    <button type="button" className="button button--outline" onClick={() => { setEditing(piscina); setOpenForm(true); }}>
                      <Pencil size={16} /> Modifica
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </section>

      <section className="section-block pool-history-section">
        <div className="section-heading">
          <div><p className="eyebrow">Storico</p><h2>Cosa e stato misurato</h2></div>
          <div className="section-heading__actions">
            <label className="inline-field">
              <span>Struttura</span>
              <select value={siteId} onChange={(event) => { setSiteId(event.target.value); setPoolId(""); }}>
                <option value="">Tutte</option>
                {perStruttura.map((gruppo) => <option key={gruppo.siteId} value={gruppo.siteId}>{gruppo.siteName}</option>)}
              </select>
            </label>
            <label className="inline-field">
              <span>Vasca</span>
              <select value={poolId} onChange={(event) => setPoolId(event.target.value)}>
                <option value="">Tutte</option>
                {vascheFiltrabili.map((piscina) => <option key={piscina.id} value={piscina.id}>{piscina.name}</option>)}
              </select>
            </label>
            <label className="inline-field"><span>Dal</span><input type="date" value={dal} onChange={(event) => setDal(event.target.value)} /></label>
            <label className="inline-field"><span>Al</span><input type="date" value={al} onChange={(event) => setAl(event.target.value)} /></label>
            <label className="inline-field">
              <span>Azione</span>
              <select value={azione} onChange={(event) => setAzione(event.target.value as PoolActionKey | "")}>
                <option value="">Tutte</option>
                {POOL_ACTIONS.map((voce) => <option key={voce.key} value={voce.key}>{voce.label}</option>)}
              </select>
            </label>
            <PrintButton />
          </div>
        </div>

        {/* Il foglio che sostituisce il modulo consegnato al condominio: struttura, vasca e periodo. */}
        <PrintSheetHeading
          title="Registro piscine"
          details={[
            strutturaScelta?.siteName ?? "Tutte le strutture",
            vascaScelta?.name ?? "Tutte le vasche",
            periodoLeggibile(dal, al),
            azioneScelta ? `Solo con: ${azioneScelta.label}` : null,
          ]}
        />

        {readings.isLoading && <p className="loading-copy">Caricamento storico...</p>}
        {!readings.isLoading && letture.length === 0 && (
          <EmptyState title="Nessuna rilevazione">Le letture registrate compariranno qui, dalla piu recente.</EmptyState>
        )}
        {letture.length > 0 && (
          <div className="week-board-scroll">
            <table className="pool-history">
              <thead>
                <tr>
                  <th scope="col">Giorno</th>
                  <th scope="col">Ora</th>
                  <th scope="col">Struttura</th>
                  <th scope="col">Vasca</th>
                  <th scope="col">Cloro</th>
                  <th scope="col">pH</th>
                  <th scope="col">Azioni</th>
                  <th scope="col">Reintegro</th>
                  <th scope="col">Prodotti</th>
                  <th scope="col">Note</th>
                  <th scope="col">Chi ha misurato</th>
                </tr>
              </thead>
              <tbody>
                {letture.map((lettura) => (
                  <tr key={lettura.id} className={lettura.outOfRange ? "pool-history__fuori" : undefined}>
                    <td>{giorno(lettura.measuredAt)}</td>
                    <td>{ora(lettura.measuredAt)}</td>
                    <td>{lettura.siteName}</td>
                    <td>{lettura.poolName}</td>
                    <td>{valore(lettura.chlorine, lettura.chlorineVerdict)}</td>
                    <td>{valore(lettura.ph, lettura.phVerdict)}</td>
                    <td>{poolActionShortLabels(lettura).join(", ") || "-"}</td>
                    <td>{lettura.waterRefillLiters != null ? formatLiters(lettura.waterRefillLiters) : lettura.waterRefill ? "si" : "-"}</td>
                    <td>{lettura.products ?? "-"}</td>
                    <td>{lettura.notes ?? "-"}</td>
                    <td>{lettura.recordedByName}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={7}>Reintegro d'acqua del periodo</th>
                  <td colSpan={4}>{formatLiters(litriDelPeriodo)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
